<template>
  <div class="flex flex-col bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm w-full h-full select-none">
    <div class="h-[3px] bg-mc-red shrink-0"></div>

    <!-- Header -->
    <div class="px-4 pt-3 pb-2 border-b border-mc-steel dark:border-line-on-dark shrink-0">
      <p class="font-display font-extrabold text-[15px] leading-tight text-mc-ink dark:text-on-dark truncate">
        {{ widget.title }}
      </p>
    </div>

    <!-- Indicator -->
    <div class="flex flex-col items-center gap-3 flex-1 justify-center p-4">
      <!-- Glowing dot -->
      <div
        class="rounded-full transition-all duration-500"
        :class="[
          !isCustomMode
            ? (isOn
                ? 'bg-green-400 shadow-[0_0_18px_6px_rgba(74,222,128,0.5)]'
                : 'bg-mc-steel dark:bg-mc-ink shadow-none')
            : '',
          dotSize
        ]"
        :style="customDotStyle"
      ></div>

      <!-- Label -->
      <span
        class="font-display font-black transition-colors duration-300"
        :class="[
          labelSize,
          !isCustomMode
            ? (isOn ? 'text-green-500 dark:text-green-400' : 'text-ink-muted dark:text-on-dark-muted')
            : ''
        ]"
        :style="customLabelStyle"
      >
        {{ displayLabel }}
      </span>
    </div>

    <!-- Last update -->
    <p class="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted px-4 pb-3 text-center shrink-0">
      {{ formattedLastUpdate }}
    </p>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { utcToLocal } from '@/tools/TimeFunctions';

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const sourceNotFound = computed(() =>
  props.sourceErrors?.[props.widget.source]?.type === 'not_found'
    ? props.widget.source : null
);

const TRUTHY = new Set(['true', '1', 'yes', 'on', 'active', 'enabled', 'high']);

const rawValue = computed(() => {
  const src = props.widget.source;
  const field = props.widget.field;
  if (!src || !field) return null;
  return props.streamData[src]?.lastValue?.[field] ?? null;
});

const isCustomMode = computed(() => props.widget.mode === 'custom');

const matchedState = computed(() => {
  if (!isCustomMode.value || rawValue.value === null) return null;
  const states = props.widget.states ?? [];
  const raw = String(rawValue.value).toLowerCase();
  return states.find(s => s.value.toLowerCase() === raw) ?? null;
});

const isOn = computed(() => {
  const v = rawValue.value;
  if (v === null || v === undefined) return false;
  return TRUTHY.has(String(v).toLowerCase().trim());
});

const onLabel = computed(() => props.widget.onLabel?.trim() || 'ON');
const offLabel = computed(() => props.widget.offLabel?.trim() || 'OFF');

const displayLabel = computed(() => {
  if (sourceNotFound.value) return 'N/A';
  if (rawValue.value === null) return '-';
  if (isCustomMode.value) {
    return matchedState.value ? matchedState.value.label : String(rawValue.value);
  }
  return isOn.value ? onLabel.value : offLabel.value;
});

function hexToRgb(hex) {
  const match = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) return { r: 107, g: 114, b: 128 }; // fallback to neutral gray
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16)
  };
}

const customDotStyle = computed(() => {
  if (!isCustomMode.value) return {};
  if (!matchedState.value) return { backgroundColor: '#6b7280' };
  const { r, g, b } = hexToRgb(matchedState.value.color);
  return {
    backgroundColor: matchedState.value.color,
    boxShadow: `0 0 18px 6px rgba(${r},${g},${b},0.5)`
  };
});

const customLabelStyle = computed(() => {
  if (!isCustomMode.value) return {};
  return { color: matchedState.value ? matchedState.value.color : '#e5e7eb' };
});

const formattedLastUpdate = computed(() => {
  if (sourceNotFound.value) return `Source "${sourceNotFound.value}" not found`;
  const src = props.widget.source;
  if (!src) return 'No source';
  const data = props.streamData[src];
  if (data?.lastUpdate) {
    return `Last update: ${utcToLocal(data.lastUpdate, props.timeFormat)}`;
  }
  return 'Waiting for data...';
});

const dotSize = computed(() => 'w-16 h-16');
const labelSize = computed(() => 'text-3xl');
</script>
